import React, { FC, ReactNode, useState } from 'react'
import moment from 'moment-timezone'
import { Button } from 'transfix-ui/components/Button'
import { ExpandableRow } from 'transfix-ui/components/ExpandableRow'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import Box from 'transfix-ui/core/Box'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { calcRem } from 'transfix-ui/theme/typography'
import { SpotBiddingRule } from 'apps/marketplace/apiTypes/spotBiddingConfigs'
import { useUpdateSpotBiddingRule } from 'apps/marketplace/apiTypes/spotBiddingRules'
import {
  NotAvailableIcon,
  EditIcon,
  DeleteIcon,
  DryVanIcon,
  GlobeIcon,
  ClockIcon,
  LaneArrowIcon,
  StepPricingIcon,
} from 'transfix-icons'
import { FORMATS } from 'constants/datetime'
import { EQUIPMENT_OPTIONS } from '../constants/spotBiddingConfig'
import { floatToPercentage } from '../utils/shipperStrikePrice'
import { HOURS_IN_DAY } from './form-components/NumDaysOut'

interface IStrikePriceRuleProps {
  rule: SpotBiddingRule
  ruleEditHandler: (rule?: SpotBiddingRule) => void
  ruleDeleteSuccessHandler: () => void
}

/**
 * "!important" CSS overrides are required to
 * override default styles on the ExpandableRow
 * DPL component.
 */
const useStyles = makeStyles(theme => ({
  row: {
    borderRadius: '5px',
    marginBottom: theme.spacing(1),
    padding: `${theme.spacing(1.2)} !important`,
    backgroundColor: `${brandColors.coolGray1} !important`,
    '&:hover': {
      backgroundColor: `${brandColors.coolGray1} !important`,
    },
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
  },
  rowIcon: {
    display: 'flex',
    alignItems: 'end',
  },
  rowLabel: {
    display: 'inline-block',
    fontWeight: 700,
  },
  rowAction: {
    display: 'inline-block',
    fontWeight: 400,
    color: brandColors.coolGray6,
  },
  details: {
    marginBottom: theme.spacing(1),
    backgroundColor: `${brandColors.white} !important`,
    borderRadius: '5px',
    padding: `${theme.spacing(1)} !important`,
  },
  fieldLabel: {
    color: brandColors.coolGray5,
    textTransform: 'uppercase',
    fontSize: calcRem(11),
    fontWeight: 700,
    letterSpacing: calcRem(2),
    marginBottom: theme.spacing(0.5),
  },
  fieldValue: {
    color: brandColors.coolGray8,
    fontSize: calcRem(14),
    fontWeight: 500,
    marginBottom: theme.spacing(1.5),
  },
}))

export const formatDate = (dateStr: string): string => {
  return moment(dateStr).format(FORMATS.dateFormatShort)
}

export const getRuleIcon = (rule: SpotBiddingRule): ReactNode => {
  if (rule.ruleAction === 'DONT_BID') return <NotAvailableIcon className='large' />
  if (rule.countLoadsQuotedCriteria) return <StepPricingIcon className='large' />
  if (rule.mileageCriteria) return <DryVanIcon className='large' />
  if (rule.geographicCriteria) return <GlobeIcon className='large' />
  if (rule.laneGeographicCriteria) return <LaneArrowIcon className='large' />
  if (rule.dateCriteria || rule.isWeekend || rule.proximalHourCriteria || rule.isSameDay)
    return <ClockIcon className='large' />
  return <GlobeIcon className='large' />
}

/**
 * Converts cost adjustment to readable value.
 * @example getCostAdjustment(rule) // '+15%'
 */
export const getCostAdjustment = (rule: SpotBiddingRule): string => {
  if (rule.ruleAction !== 'APPLY_PREMIUM') return ''
  const { amount } = rule.spotPremium
  return `${amount >= 0 ? '+' : '-'}${Math.abs(floatToPercentage(amount))}%`
}

export const getRuleAction = (rule: SpotBiddingRule): string => {
  if (rule.ruleAction === 'DONT_BID') return 'DNB'
  if (rule.ruleAction === 'APPLY_PREMIUM') {
    const { amount } = rule.spotPremium
    return `TCM ${amount >= 0 ? '+' : '-'}${Math.abs(floatToPercentage(amount))}%`
  }
  return ''
}

/**
 * Formats time string.
 * @example formatTime('13:00') // 1:00 PM
 * @todo Try replacing with moment or transfix-ui util.
 */
export const formatTime = (time: string): string => {
  const hour = parseInt(time.substr(0, 2), 10)
  const convertedHour = hour % 12 || 12
  const period = hour < 12 ? 'AM' : 'PM'
  const minutes = time.substr(3, 2)
  return `${convertedHour}:${minutes} ${period}`
}

/**
 * Converts days out rules into human-readable summary.
 * @example getDaysOutDetails(rule) // Next Day 1:00 PM EST
 */
export const getDaysOutDetails = (rule: SpotBiddingRule): string => {
  if (rule.proximalHourCriteria) {
    const { rangeEnd: endHour, rangeStart: startHour } = rule.proximalHourCriteria
    let ruleDetailsPrefix = ''
    let daysOut = 0
    let timeDetails = ''
    let appointmentType = ''
    let timeZone = ''
    const endDay = Math.floor(endHour / HOURS_IN_DAY)
    const startDay = Math.floor(startHour / HOURS_IN_DAY)
    if (rule.ruleStartTimeCriteria) {
      timeDetails = ` ${formatTime(rule.ruleStartTimeCriteria.rangeStart)} EST`
    }
    if (rule.appointmentTimeCriteria) {
      appointmentType = rule.appointmentTimeCriteria.pickupRangeStart ? 'PU' : 'DEL'
      timeDetails = ` ${appointmentType}`
      timeDetails +=
        appointmentType === 'PU'
          ? ` ${rule.appointmentTimeCriteria.pickupRangeStart} - ${rule.appointmentTimeCriteria.pickupRangeEnd} `
          : ` ${rule.appointmentTimeCriteria.deliveryRangeStart} - ${rule.appointmentTimeCriteria.deliveryRangeEnd} `
      if (rule.appointmentTimeCriteria.timezoneOverride) {
        timeZone = moment.tz(rule.appointmentTimeCriteria.timezoneOverride).format('z')
        timeDetails += timeZone
      } else {
        timeDetails += 'EST'
      }
    }
    if (startHour === 0 || startHour === null) {
      daysOut = endHour > 23 ? endDay : endHour
      ruleDetailsPrefix = `Less than ${daysOut}`
    } else if (endHour - startHour === HOURS_IN_DAY) {
      daysOut = endHour > 23 ? startDay : startHour
      if (daysOut === 1 && (endHour > 23 || startHour > 23)) {
        return `Next Day${timeDetails}`
      }
      ruleDetailsPrefix = `Exactly ${daysOut}`
    } else {
      daysOut = startHour > 23 ? startDay - 1 : startHour
      ruleDetailsPrefix = `More than ${daysOut}`
    }
    if (startHour < 24 && endHour < 24) {
      return `${ruleDetailsPrefix} ${daysOut > 1 ? 'hours' : 'hour'} out${timeDetails}`
    }
    return `${ruleDetailsPrefix} ${daysOut > 1 ? 'days' : 'day'} out${timeDetails}`
  }
  return ''
}

export const formatSameDayCriteria = (rule: SpotBiddingRule): string => {
  if (rule.ruleStartTimeCriteria) {
    return `Same Day ${formatTime(rule.ruleStartTimeCriteria.rangeStart)} EST`
  }
  return 'Same Day'
}

export const formatEquipmentType = (rule: SpotBiddingRule): string => {
  if (rule.parentEquipmentKey) {
    const equipmentType = EQUIPMENT_OPTIONS.find(opt => opt.value === rule.parentEquipmentKey)
    if (equipmentType) {
      return equipmentType.label
    }
  }
  return 'Equipment'
}

export const formatDateCriteria = (rule: SpotBiddingRule): string => {
  if (rule.isWeekend) {
    return 'Weekend'
  }
  if (rule.isSameDay) {
    return formatSameDayCriteria(rule)
  }
  if (rule.proximalHourCriteria) {
    return getDaysOutDetails(rule)
  }
  if (rule.dateCriteria) {
    const start = moment(rule.dateCriteria.rangeStart).format(FORMATS.dateFormatShort)
    const end = moment(rule.dateCriteria.rangeEnd).format(FORMATS.dateFormatShort)
    return start === end ? start : `${start} — ${end}`
  }
  return ''
}

export const getPrimaryRuleDetails = (rule: SpotBiddingRule): string => {
  if (rule.parentEquipmentKey) {
    return formatEquipmentType(rule)
  }
  if (rule.mileageCriteria) {
    const { rangeEnd, rangeStart } = rule.mileageCriteria
    const mileage =
      typeof rangeEnd === 'number' ? `${rangeStart}-${rangeEnd} Miles` : `${rangeStart}+ Miles`
    return rule.geographicCriteria ? `${mileage} (${rule.geographicCriteria.value})` : mileage
  }
  if (rule.geographicCriteria) {
    return rule.geographicCriteria.value
  }
  if (rule.laneGeographicCriteria) {
    return `${rule.laneGeographicCriteria.pickupValue} — ${rule.laneGeographicCriteria.deliveryValue}`
  }
  if (rule.countLoadsQuotedCriteria) {
    const pickup =
      rule.countLoadsQuotedCriteria.pickupType === 'Any Market'
        ? rule.countLoadsQuotedCriteria.pickupType
        : rule.countLoadsQuotedCriteria.pickupValue
    const delivery =
      rule.countLoadsQuotedCriteria.deliveryType === 'Any Market'
        ? rule.countLoadsQuotedCriteria.deliveryType
        : rule.countLoadsQuotedCriteria.deliveryValue
    const pickupDel = rule.countLoadsQuotedCriteria.isSamePickupDelivery
      ? `${pickup}` || `${delivery}`
      : `${pickup} - ${delivery}`
    const { rangeStart } = rule.countLoadsQuotedCriteria
    const { rangeEnd } = rule.countLoadsQuotedCriteria
    let range = ''
    if (rangeEnd && rangeStart) {
      range = `Between ${rangeStart} - ${rangeEnd} shipments`
    } else if (rangeEnd) {
      range = `Less than ${rangeEnd} shipments`
    } else {
      range = `More than/equal to ${rangeStart} shipments`
    }
    return `${pickupDel} ; ${range} `
  }
  if (rule.dateCriteria) {
    const start = moment(rule.dateCriteria.rangeStart).format(FORMATS.dateFormatShort)
    const end = moment(rule.dateCriteria.rangeEnd).format(FORMATS.dateFormatShort)
    return start === end ? start : `${start} — ${end}`
  }
  if (rule.proximalHourCriteria) {
    return getDaysOutDetails(rule)
  }
  if (rule.isWeekend) {
    return 'Weekend'
  }
  if (rule.isSameDay) {
    return formatSameDayCriteria(rule)
  }
  return ''
}

export const StrikePriceRule: FC<IStrikePriceRuleProps> = ({
  rule,
  ruleDeleteSuccessHandler,
  ruleEditHandler,
}) => {
  const classes = useStyles()
  const {
    error: ruleUpdateError,
    loading: ruleUpdatePending,
    mutate: updateRule,
  } = useUpdateSpotBiddingRule({
    spotBiddingRuleId: rule.id,
  })
  const [deleteConfirmationMode, setDeleteConfirmationMode] = useState(false)
  const deleteRule = async () => {
    await updateRule({
      spotBiddingRule: {
        deletedAt: moment().toISOString(),
      },
    })
  }
  const editHandler = () => ruleEditHandler(rule)
  const deleteHandler = () => setDeleteConfirmationMode(true)
  const confirmDeleteHandler = async () => {
    await deleteRule()
    ruleDeleteSuccessHandler()
  }
  const cancelDeleteHandler = () => setDeleteConfirmationMode(false)
  const rowLabelColor = rule.ruleAction === 'DONT_BID' ? brandColors.error : brandColors.info

  return (
    <ExpandableRow
      id={String(rule.id)}
      rowClass={classes.row}
      row={
        <Box className={classes.rowContent}>
          <Box mr={1} className={classes.rowIcon} color={rowLabelColor}>
            {getRuleIcon(rule)}
          </Box>
          <Box>
            <Box mr={1} className={classes.rowLabel} color={rowLabelColor}>
              {getPrimaryRuleDetails(rule)}
            </Box>
            <Box className={classes.rowAction}>{getRuleAction(rule)}</Box>
          </Box>
        </Box>
      }
      detailsClass={classes.details}
      details={
        <Box>
          {rule.ruleAction === 'APPLY_PREMIUM' && (
            <>
              <Box className={classes.fieldLabel}>Adjustment Percentage</Box>
              <Box className={classes.fieldValue}>{getCostAdjustment(rule)}</Box>
            </>
          )}
          <Box className={classes.fieldLabel}>Reason</Box>
          <Box className={classes.fieldValue}>{rule.reason}</Box>
          <Box className={classes.fieldLabel}>Date Criteria</Box>
          <Box className={classes.fieldValue}>{formatDateCriteria(rule)}</Box>
          {deleteConfirmationMode ? (
            <>
              <Box mb={1}>
                {ruleUpdateError ? (
                  <InlineMessage
                    type={INLINE_MESSAGE_VARIANTS.ALERT}
                    message={ruleUpdateError?.message}
                  />
                ) : (
                  <>Are you sure?</>
                )}
              </Box>
              <Box display='flex' justifyContent='space-between'>
                <IconButton
                  Icon={DeleteIcon}
                  color='secondary'
                  size='small'
                  onClick={confirmDeleteHandler}
                  disabled={ruleUpdatePending}
                  label='Yes, Delete'
                />
                <Button
                  size='small'
                  color='tertiary'
                  onClick={cancelDeleteHandler}
                  disabled={ruleUpdatePending}>
                  Cancel
                </Button>
              </Box>
            </>
          ) : (
            <Box display='flex' justifyContent='space-between'>
              <IconButton
                Icon={EditIcon}
                color='secondary'
                size='small'
                onClick={editHandler}
                label='Edit'
              />
              <IconButton
                Icon={DeleteIcon}
                size='small'
                color='tertiary'
                onClick={deleteHandler}
                label='Delete'
              />
            </Box>
          )}
        </Box>
      }
    />
  )
}
